import React, { useState, useEffect, forwardRef } from 'react';
import { Box, Grid, TextField, Typography } from '@mui/material';
import MaskedInput from 'react-text-mask';
import zxcvbn from 'zxcvbn';
import { useUser } from '../../contexts/UserContext';
import { ParentFormProps, TextMaskCustomProps } from '../../types';
import { isValidPassword } from '../../utils';

const TextMaskCustom = forwardRef<MaskedInput, TextMaskCustomProps>(function TextMaskCustom(props, ref) {
    const { onChange, placeholder, ...other } = props;

    return (
        <MaskedInput
            {...other}
            ref={ref}
            mask={[/\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
            placeholderChar={'\u2000'}
            showMask={!!placeholder}
            onChange={onChange}
        />
    );
});

const ParentForm: React.FC<ParentFormProps> = ({
    title,
    subtitle,
    disableInputs,
    onParentDataChange,
    onPasswordChange,
}) => {
    const { user } = useUser();
    const [isPhoneFocused] = useState(false);

    const [parentData, setParentData] = useState({
        fname: user?.fname || '',
        lname: user?.lname || '',
        email: user?.email || '',
        phone: user?.phone || '',
        password: '',
        confirmPassword: '',
    });

    const [passwordStrength, setPasswordStrength] = useState(0);

    useEffect(() => {
        if (user) {
            setParentData({
                fname: user.fname,
                lname: user.lname,
                email: user.email,
                phone: user.phone,
                password: '',
                confirmPassword: '',
            });
        }
    }, [user]);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        const updatedData = { ...parentData, [name]: value };
        setParentData(updatedData);

        if (onParentDataChange) {
            onParentDataChange(updatedData);
        }

        if (name === 'confirmPassword' || name === 'password') {
            if (
                parentData.password === parentData.confirmPassword &&
                parentData.password !== '' &&
                parentData.confirmPassword !== ''
            ) {
                onPasswordChange?.(parentData.password);
            } else {
                onPasswordChange?.('');
            }
        }
    };

    useEffect(() => {
        const strength = zxcvbn(parentData.password).score;
        setPasswordStrength(strength);
    }, [parentData.password]);

    return (
        <Box className="single-form">
            <Grid container spacing={2} className="text-left">
                <Grid item xs={12} md={3}></Grid>
                <Grid item xs={12} md={2}>
                    <h2>{title}</h2>
                    <h3>{subtitle}</h3>
                </Grid>
                <Grid item xs={12} md={4}>
                    <div>
                        <TextField
                            name="fname"
                            required
                            label="Parent First Name"
                            variant="standard"
                            value={parentData.fname}
                            disabled={disableInputs}
                            onChange={handleInputChange}
                            inputProps={{ maxLength: 50 }}
                        />
                    </div>
                    <div>
                        <TextField
                            name="lname"
                            required
                            label="Parent Last Name"
                            variant="standard"
                            value={parentData.lname}
                            disabled={disableInputs}
                            onChange={handleInputChange}
                            inputProps={{ maxLength: 50 }}
                        />
                    </div>
                    <div>
                        <TextField
                            name="email"
                            required
                            label="Parent Email"
                            type="email"
                            variant="standard"
                            value={parentData.email}
                            disabled={disableInputs}
                            onChange={handleInputChange}
                            inputProps={{ maxLength: 100 }}
                        />
                    </div>
                    <div className={disableInputs ? 'mb-10' : ''}>
                        <TextField
                            name="phone"
                            required
                            label="Parent Phone Number"
                            type="tel"
                            variant="standard"
                            value={parentData.phone}
                            disabled={disableInputs}
                            onChange={handleInputChange}
                            InputProps={{
                                inputComponent: TextMaskCustom as any,
                                inputProps: {
                                    placeholder: isPhoneFocused ? '___-___-____' : null,
                                },
                            }}
                        />
                    </div>
                    {!disableInputs && <>

                        <div>
                            <TextField
                                name="password"
                                required
                                label="Password"
                                type="password"
                                variant="standard"
                                value={parentData.password}
                                disabled={disableInputs}
                                onChange={handleInputChange}
                                inputProps={{ maxLength: 20 }}
                            />
                            {parentData.password.length > 0 && <>
                                <Typography variant="caption" color={passwordStrength > 2 ? 'primary' : 'error'}>
                                    Password Strength: {['Very Weak', 'Weak', 'Fair', 'Good', 'Strong'][passwordStrength]}.
                                </Typography>
                            </>}
                            {
                                !isValidPassword(parentData.password) && <>
                                  <Typography variant="subtitle1" color='error'> A minimum 8 characters password contains a combination of <strong>uppercase, lowercase letter</strong>,<strong>numbers</strong> and special characters like <strong>@$!%*#?&</strong></Typography>
                              </>
                            }

                        </div>
                        <div className="mb-10">
                            <TextField
                                name="confirmPassword"
                                required
                                label="Confirm Password"
                                type="password"
                                variant="standard"
                                value={parentData.confirmPassword}
                                disabled={disableInputs}
                                onChange={handleInputChange}
                                inputProps={{ maxLength: 20 }}
                                error={
                                    parentData.password !== parentData.confirmPassword &&
                                    parentData.confirmPassword !== ''
                                }
                                helperText={
                                    parentData.password !== parentData.confirmPassword &&
                                        parentData.confirmPassword !== ''
                                        ? 'Passwords do not match'
                                        : ''
                                }
                            />
                        </div>
                    </>}
                </Grid>
            </Grid>
        </Box>
    );
};

export default ParentForm;
